import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import { TranslationService } from '@lib/i18n/data-access';
import { ConfirmModalData, ModalService, SprConfirmModalComponent } from '@platform-workspace/design-system';
import { filter } from 'rxjs';

export abstract class DiscardConfirmBaseComponent<CloseAction = void | null | boolean> {
  private readonly discardConfirmModalService = inject(ModalService);
  private readonly discardConfirmTranslationService = inject(TranslationService);
  private readonly discardConfirmDestroyRef = inject(DestroyRef);

  abstract form: AbstractControl;
  abstract closeAction: (action?: CloseAction) => void;

  onClose(): void {
    if (this.isReadonly() || this.form.pristine) {
      this.closeAction();
      return;
    }

    this.discardConfirmModalService
      .open<ConfirmModalData, boolean>(SprConfirmModalComponent, {
        modalData: this.getDiscardConfirmModalData(),
      })
      .pipe(
        // Emit only on explicit 'false' (clicking "Discard Changes").
        // 'true' ("Continue Editing") and 'undefined' (Close icon) are ignored.
        filter((result) => result === false),
        takeUntilDestroyed(this.discardConfirmDestroyRef),
      )
      .subscribe(() => this.closeAction());
  }

  protected isReadonly(): boolean {
    return false;
  }

  protected getDiscardConfirmModalData(): ConfirmModalData {
    return {
      title: this.discardConfirmTranslationService.translate('shared.unsavedChanges.title'),
      body: this.discardConfirmTranslationService.translate('shared.unsavedChanges.body'),
      cancelButtonText: this.discardConfirmTranslationService.translate('common.Discard Changes'),
      confirmButtonText: this.discardConfirmTranslationService.translate('common.Continue Editing'),
    };
  }
}
